import { Link } from "react-router-dom";
import {
  Bell,
  ChevronsUpDown,
  LogOut,
  Settings as SettingsIcon,
  SunMoon,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuthStore } from "@/lib/auth";
import { useUserMe } from "@/lib/user-me";
import { UserAvatar } from "./UserAvatar";

/**
 * Bottom-of-sidebar block. Personal-only — workspace items moved to
 * `SidebarWorkspaceMenu` at the top, which is the right place for
 * them per the enterprise sidebar pattern (Slack / Notion / Linear).
 *
 * Click → upward Radix dropdown with: Account settings, Notification
 * preferences, Theme, Sign out.
 */
export function SidebarUserMenu() {
  const me = useUserMe();
  const email = useAuthStore((s) => s.email);
  const clear = useAuthStore((s) => s.clear);

  const u = me.data;
  const displayName =
    [u?.firstName, u?.lastName].filter(Boolean).join(" ").trim() ||
    (email ?? "").split("@")[0].replace(/[._-]/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) ||
    "Account";
  const initials = computeInitials(u?.firstName, u?.lastName, email);
  const avatarUrl = u?.avatarUrl ?? null;

  const handleSignOut = () => {
    clear();
    const authUrl = import.meta.env.VITE_AUTH_APP_URL ?? "http://localhost:5174";
    // Hard nav so any cached react-query state for the previous user is dropped.
    window.location.assign(`${authUrl}/sign-in`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="w-full flex items-center gap-3 rounded-md px-3 py-2.5 text-left transition-colors hover:bg-sidebar-accent data-[state=open]:bg-sidebar-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Open account menu"
        >
          <UserAvatar src={avatarUrl} fallback={initials} size="md" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{displayName}</p>
            <p className="text-[11px] text-sidebar-muted truncate">
              {email ?? ""}
            </p>
          </div>
          <ChevronsUpDown className="h-3.5 w-3.5 text-sidebar-muted shrink-0" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="top"
        align="start"
        sideOffset={8}
        className="w-[var(--radix-dropdown-menu-trigger-width)] min-w-[240px]"
      >
        <DropdownMenuLabel className="flex items-center gap-3 px-3 py-3">
          <UserAvatar src={avatarUrl} fallback={initials} size="lg" />
          <div className="min-w-0">
            <p className="text-sm font-medium truncate">{displayName}</p>
            <p className="text-[11px] text-muted-foreground font-normal truncate">
              {email ?? ""}
            </p>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuGroup>
          <DropdownMenuItem asChild>
            <Link to="/account/profile">
              <SettingsIcon className="h-4 w-4 text-muted-foreground" />
              <span>Account settings</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem asChild>
            <Link to="/account/notifications">
              <Bell className="h-4 w-4 text-muted-foreground" />
              <span>Notification preferences</span>
            </Link>
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={(e) => e.preventDefault()}>
            <SunMoon className="h-4 w-4 text-muted-foreground" />
            <span className="flex-1">Theme</span>
            <span className="text-xs text-muted-foreground">Auto</span>
          </DropdownMenuItem>
        </DropdownMenuGroup>

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={handleSignOut}
          className="text-destructive focus:text-destructive focus:bg-destructive/10"
        >
          <LogOut className="h-4 w-4" />
          <span>Sign out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function computeInitials(
  firstName?: string | null,
  lastName?: string | null,
  email?: string | null,
): string {
  const f = (firstName ?? "").trim().charAt(0).toUpperCase();
  const l = (lastName ?? "").trim().charAt(0).toUpperCase();
  if (f || l) return (f + l) || f || l;
  // Fall back to email local-part initial.
  const local = (email ?? "").split("@")[0] ?? "";
  return (local.charAt(0).toUpperCase() || "?");
}
